import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Select,
  Input,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Box,
} from '@chakra-ui/react';
import { FiTruck, FiClock } from 'react-icons/fi';
import { RegistrationData } from '../index';
import DistanceCalculator from './DistanceCalculator';

interface FreightShippingProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const FreightShipping: React.FC<FreightShippingProps> = ({
  data,
  updateData,
}) => {
  const sectionBg = useColorModeValue('gray.50', 'gray.700');

  const updateFreightShipping = (field: string, value: string) => {
    updateData({
      ...data,
      freightShipping: {
        ...data.freightShipping,
        [field]: value,
      },
    });
  };

  const freightPolicyOptions = [
    {
      value: 'pre-paid',
      label: 'Pre-Paid',
      description: 'Freight charge is included in the product cost being invoiced'
    },
    {
      value: 'pre-pay-add',
      label: 'Pre-Pay & Add',
      description: 'Vendor selects shipper and adds a freight line to the invoice'
    }
  ];

  const fobPointOptions = [
    {
      value: 'origin',
      label: 'Origin',
      description: 'Title changes at the point of initial shipment'
    },
    {
      value: 'destination',
      label: 'Destination',
      description: 'Title changes at the point of delivery'
    }
  ];

  return (
    <VStack spacing={8} align="stretch">
      {/* Freight Policy Section */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiTruck} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Freight Policy
              </Text>
            </HStack>

            <FormControl isRequired>
              <FormLabel fontWeight="medium">
                What is the Freight Policy?
              </FormLabel>
              <Select
                placeholder="Select a Freight Policy"
                value={data.freightShipping?.freightPolicy || ''}
                onChange={(e) => updateFreightShipping('freightPolicy', e.target.value)}
              >
                {freightPolicyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontWeight="medium">
                FOB Point
              </FormLabel>
              <Select
                placeholder="Select a FOB Point"
                value={data.freightShipping?.fobPoint || ''}
                onChange={(e) => updateFreightShipping('fobPoint', e.target.value)}
              >
                {fobPointOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Shipping Timing Section */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiClock} color="green.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Shipping Timing
              </Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl>
                <FormLabel fontWeight="medium">
                  What is the Standard Lead Time?
                </FormLabel>
                <Input
                  placeholder="e.g., 2-3 weeks"
                  value={data.freightShipping?.standardLeadTime || ''}
                  onChange={(e) => updateFreightShipping('standardLeadTime', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="medium">
                  Estimated Transit Days
                </FormLabel>
                <Input
                  placeholder="e.g., 5-7 days"
                  value={data.freightShipping?.estimatedTransitDays || ''}
                  onChange={(e) => updateFreightShipping('estimatedTransitDays', e.target.value)}
                />
              </FormControl>
            </SimpleGrid>

            <Box bg={sectionBg} p={4} borderRadius="md">
              <Text fontSize="sm" color="gray.600">
                <strong>Estimated Transit Days:</strong> Please provide the best possible estimate of calendar 
                days for Ready To Ship product to travel from the manufacturing plant/vendor warehouse 
                to Tangram's warehouse located in Santa Fe Springs, CA (Zip Code 90670)
              </Text>
            </Box>

            {/* Distance Calculator */}
            <DistanceCalculator
              vendorAddress={data.companyInfo?.mainAddress || data.companyInfo?.shipAddress || ''}
              vendorCity={data.companyInfo?.city || ''}
              vendorState={data.companyInfo?.state || ''}
              vendorPostalCode={data.companyInfo?.postalCode || ''}
              vendorCountry={data.companyInfo?.country || ''}
            />
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default FreightShipping; 